import { getApiBaseUrl } from '../../config/runtimeConfig';
import { sessionFetch } from '../../services/session';
import { listCustomers } from '../customers/customerService';
import { listContainers } from '../container-planning/containerPlanningService';
import { listInventory } from '../inventory/inventoryService';



function httpError(message, status) {
  const error = new Error(message);
  error.status = status;
  return error;
}

async function request(path, options = {}) {
  let response;
  try {
    response = await sessionFetch(`${getApiBaseUrl()}${path}`, {
      credentials: 'include',
      ...options,
      headers: { Accept: 'application/json', ...options.headers },
      signal: AbortSignal.timeout(20000),
    });
  } catch (error) {
    throw httpError(error.name === 'TimeoutError'
      ? 'The request timed out. Please try again.'
      : 'Unable to connect. Please check your connection and try again.');
  }

  const data = await response.json().catch(() => null);
  if (!response.ok) {
    throw httpError(
      typeof data?.message === 'string' ? data.message : 'Unable to complete the dashboard request.',
      response.status,
    );
  }
  return data;
}

function monthKey(value) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

function lastSixMonths(style) {
  const now = new Date();
  return Array.from({ length: 6 }, (_, index) => {
    const date = new Date(now.getFullYear(), now.getMonth() - (5 - index), 1);
    return {
      key: monthKey(date),
      month: date.toLocaleString('en-US', { month: style }),
    };
  });
}

function isBulkPackaging(name) {
  const key = String(name || '').toLowerCase();
  return key.includes('bulk') || key.includes('liquid') || key.includes('tank') || key.includes('drum');
}

function percent(part, total) {
  if (!total) return 0;
  return Math.max(0, Math.min(100, Math.round((Number(part) || 0) / total * 100)));
}

function shipmentProgress(kpis) {
  const count = (value) => Math.max(0, Number(value) || 0);
  const planning = count(kpis.in_planning);
  const ready = count(kpis.ready_to_ship);
  const shipped = count(kpis.shipped);
  const clearance = count(kpis.clearance);
  const total = count(kpis.planned) + planning + ready + shipped + clearance;
  const reachedReady = ready + shipped + clearance;
  const reachedShipped = shipped + clearance;
  return {
    total_containers: total,
    reached_ready_to_ship: reachedReady,
    reached_shipped: reachedShipped,
    reached_customs_clearance: clearance,
    overall_percent: percent(ready + shipped * 2 + clearance * 3, total * 3),
    lanes: [
      { label: 'Ready to Ship', value: percent(reachedReady, total), tone: 'lane' },
      { label: 'Shipped', value: percent(reachedShipped, total), tone: 'inspect' },
      { label: 'Customs Clearance', value: percent(clearance, total), tone: 'duty' },
    ],
  };
}

function roundTons(value) {
  return Math.round((Number(value) || 0) * 1000) / 1000;
}

function buildFallbackOverview({ customers, containers, summary, inventory }) {
  const kpis = {
    customers: customers.length,
    in_planning: Number(summary.total_draft) || 0,
    ready_to_ship: Number(summary.ready_to_ship) || 0,
    shipped: Number(summary.shipped) || 0,
    clearance: Number(summary.customs_clearance) || 0,
    delivered: Number(summary.delivered) || containers.filter((row) => row.status === 'Delivered').length,
    planned: Number(summary.planned) || containers.filter(row => row.status === 'Planned').length,
    cancelled: Number(summary.cancelled) || containers.filter((row) => row.status === 'Cancelled').length,
  };
  const stockMonths = lastSixMonths('short');
  const stockMap = Object.fromEntries(stockMonths.map((item) => [item.key, { general: 0, bulk: 0 }]));
  inventory.forEach((row) => {
    const key = monthKey(row.inventory_date);
    if (!stockMap[key]) return;
    const tons = (Number(row.gross_weight) || 0) / 1000;
    if (isBulkPackaging(row.packaging_type)) stockMap[key].bulk += tons;
    else stockMap[key].general += tons;
  });

  const shippedMonths = lastSixMonths('long');
  const shippedMap = Object.fromEntries(shippedMonths.map((item) => [item.key, { hc: 0, dry: 0 }]));
  containers
    .filter((row) => ['Shipped', 'CustomsClearance', 'Delivered'].includes(row.status))
    .forEach((row) => {
      const key = monthKey(row.draft_date);
      if (!shippedMap[key]) return;
      const profile = String(row.profile_name || '').toLowerCase();
      if (profile.includes('40')) shippedMap[key].hc += 1;
      if (profile.includes('20')) shippedMap[key].dry += 1;
    });

  return {
    kpis,
    stock_in: stockMonths.map((item) => ({
      month: item.month,
      general: roundTons(stockMap[item.key].general),
      bulk: roundTons(stockMap[item.key].bulk),
    })),
    shipped: shippedMonths.map((item) => ({
      month: item.month,
      hc: shippedMap[item.key].hc,
      dry: shippedMap[item.key].dry,
    })),
    shipment_progress: shipmentProgress(kpis),
  };
}

const emptyOverview = buildFallbackOverview({
  customers: [],
  containers: [],
  summary: { total_draft: 0, planned: 0, ready_to_ship: 0, shipped: 0, customs_clearance: 0, delivered: 0 },
  inventory: [],
});

export function emptyDashboard() {
  return emptyOverview;
}

async function loadFromExistingApis(filters = {}) {
  const [customers, containers, inventory] = await Promise.all([
    listCustomers(),
    listContainers({ page: 1, pageSize: 100, ...filters }).then(result => ({ ...result, containers: [...result.containers] })),
    listInventory({ page: 1, pageSize: 100, ...filters }).then(result => ({ ...result, inventory: [...result.inventory] })),
  ]);
  if (filters.warehouse_id) {
    for (let page = 2; inventory.inventory.length < inventory.total; page += 1) {
      const next = await listInventory({ page, pageSize: 100, ...filters });
      if (!next.inventory.length) break;
      inventory.inventory.push(...next.inventory);
    }
    for (let page = 2; containers.containers.length < containers.total; page += 1) {
      const next = await listContainers({ page, pageSize: 100, ...filters });
      if (!next.containers.length) break;
      containers.containers.push(...next.containers);
    }
  }
  const scopedInventory = filters.warehouse_id
    ? inventory.inventory.filter(row => Number(row.warehouse_id) === Number(filters.warehouse_id) && row.status !== 'Cancelled')
    : inventory.inventory;
  const scopedContainers = filters.warehouse_id
    ? containers.containers.filter(row => Number(row.warehouse_id) === Number(filters.warehouse_id))
    : containers.containers;
  const scopedSummary = filters.warehouse_id ? {
    total_draft: scopedContainers.filter(row => row.status === 'Planning').length,
    planned: scopedContainers.filter(row => row.status === 'Planned').length,
    ready_to_ship: scopedContainers.filter(row => row.status === 'ReadyToShip').length,
    shipped: scopedContainers.filter(row => row.status === 'Shipped').length,
    customs_clearance: scopedContainers.filter(row => row.status === 'CustomsClearance').length,
    delivered: scopedContainers.filter(row => row.status === 'Delivered').length,
    cancelled: scopedContainers.filter(row => row.status === 'Cancelled').length,
  } : containers.summary;
  return buildFallbackOverview({
    customers: filters.warehouse_id
      ? Array.from(new Set(scopedInventory.map(row => row.customer_id)))
      : customers,
    containers: scopedContainers,
    summary: scopedSummary,
    inventory: scopedInventory,
  });
}

async function loadDashboard(filters = {}) {
  const warehouseId = filters.warehouse_id ?? filters.warehouseId;
  filters = warehouseId && warehouseId !== 'all' ? { warehouse_id: warehouseId } : {};
  try {
    const params = new URLSearchParams();
    if (filters.warehouse_id) params.set('warehouse_id', String(filters.warehouse_id));
    const query = params.toString();
    const data = await request(query ? `/dashboard?${query}` : '/dashboard');
    if (data?.kpis && (!filters.warehouse_id || Number(data.warehouse_id) === Number(filters.warehouse_id))) return { ...data, shipment_progress: shipmentProgress(data.kpis) };
  } catch (error) {
    if (error.status && error.status !== 404) throw error;
  }
  try {
    return await loadFromExistingApis(filters);
  } catch (error) {
    if (!error.status || error.status === 404) return emptyDashboard();
    throw error;
  }
}

const dashboardInFlight = new Map();
export function getDashboard(filters = {}) {
  const warehouseId = filters.warehouse_id ?? filters.warehouseId;
  const normalized = warehouseId && warehouseId !== 'all' ? { warehouse_id: warehouseId } : {};
  const key = JSON.stringify([getApiBaseUrl(), String(normalized.warehouse_id || '')]);
  if (!dashboardInFlight.has(key)) {
    const pending = loadDashboard(normalized).finally(() => {
      if (dashboardInFlight.get(key) === pending) dashboardInFlight.delete(key);
    });
    dashboardInFlight.set(key, pending);
  }
  return dashboardInFlight.get(key);
}
